import { Box, Chip, LinearProgress, Stack, Tooltip, Typography, alpha } from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

/*
  CreditsBar
  ──────────────────────────────────────────────────────────────
  Compact horizontal credit indicator used at the top of the hub
  hero card and inside the chat dialog header. Mirrors the mobile
  Copilot hub credit chip but uses MUI primitives to stay native
  to the dashboard's visual language.

  Visual:
    [⚡] AI credits  ████████░░  18 / 20 used   · resets in 12 days   [Free]

  Props:
    credits — the `LectureCreditsDto` from getCopilotCreditsCall().
              null while loading / unauthenticated → render a
              placeholder shimmer-ish row so layout doesn't jump.
    compact — boolean; small variant for the chat dialog header.
*/

export default function CreditsBar({ credits, compact = false }) {
  if (!credits) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 32, opacity: 0.5 }}>
        <BoltRoundedIcon fontSize="small" />
        <Typography variant="caption" color="text.secondary">
          Loading AI credits…
        </Typography>
      </Stack>
    );
  }

  const used = Number(credits.creditsUsed || 0);
  const limit = Math.max(1, Number(credits.creditsLimit || 0));
  const remaining = Number(credits.creditsRemaining || 0);
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const lowOnCredits = remaining <= 3;

  // Days-until-reset is meaningful only on paid plans. Free is a
  // lifetime quota with no `periodEnd`, so we suppress that pill.
  let daysLeftLabel = null;
  if (credits.periodEnd) {
    const end = new Date(credits.periodEnd).getTime();
    if (Number.isFinite(end)) {
      const days = Math.max(0, Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24)));
      daysLeftLabel = `Resets in ${days} day${days === 1 ? "" : "s"}`;
    }
  }

  const planLabel = (credits.plan || "free").toString();
  const planDisplay = planLabel.charAt(0).toUpperCase() + planLabel.slice(1);

  return (
    <Stack
      direction={compact ? "row" : { xs: "column", sm: "row" }}
      spacing={compact ? 1 : 1.5}
      alignItems={compact ? "center" : { xs: "flex-start", sm: "center" }}
      sx={{ width: "100%" }}
    >
      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
        <BoltRoundedIcon
          sx={{
            fontSize: compact ? 16 : 18,
            color: lowOnCredits ? "warning.main" : "primary.main",
          }}
        />
        <Typography variant={compact ? "caption" : "body2"} fontWeight={600}>
          AI credits
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minWidth: 120, maxWidth: 360 }}>
        <Tooltip arrow title={`${used} used · ${remaining} remaining`}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={(theme) => ({
              height: compact ? 6 : 8,
              borderRadius: 99,
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                backgroundImage: lowOnCredits
                  ? `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.error.main})`
                  : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
              },
            })}
          />
        </Tooltip>
      </Box>

      <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {used} / {limit}
        </Typography>

        {daysLeftLabel ? (
          <Stack direction="row" spacing={0.4} alignItems="center">
            <AccessTimeRoundedIcon sx={{ fontSize: 13, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {daysLeftLabel}
            </Typography>
          </Stack>
        ) : null}

        <Chip
          size="small"
          label={planDisplay}
          variant="outlined"
          sx={(theme) => ({
            height: 20,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            color:
              planLabel === "yearly"
                ? theme.palette.success.dark
                : planLabel === "monthly"
                  ? theme.palette.info.dark
                  : theme.palette.text.secondary,
            borderColor:
              planLabel === "yearly"
                ? alpha(theme.palette.success.main, 0.5)
                : planLabel === "monthly"
                  ? alpha(theme.palette.info.main, 0.5)
                  : alpha(theme.palette.text.secondary, 0.35),
          })}
        />
      </Stack>
    </Stack>
  );
}
